/**
 * Configurações > Permissões (ADR-0034, #1190): o que cada pessoa pode, além
 * ou aquém do papel.
 *
 * ── Duas telas, na mesma seção ───────────────────────────────────────────
 *
 * Escolher a pessoa e ajustar as permissões dela são os dois passos da prévia
 * aprovada ("1 · Escolher a pessoa", "2 · Permissões da pessoa"). O rascunho
 * de cada pessoa fica na seção enquanto ela está aberta: voltar à lista não o
 * perde, e a linha da pessoa mostra "Não salvo".
 *
 * ── A tela trava, a API decide ───────────────────────────────────────────
 *
 * O que a API recusaria aparece travado, com o motivo: a dependência, o que é
 * só da Cliqfy, os limites de quem só gerencia permissões. É para a pessoa
 * saber antes de salvar. A API confere tudo de novo, e o que ela devolve é o
 * que a tela passa a mostrar.
 */

import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi } from '@cliqcrm/contracts';
import type {
  CatalogoDePermissoes,
  PapelDaOrganizacao,
  PermissoesDaPessoa,
  PessoaDasPermissoes,
  SalvarPermissoesDaPessoa,
} from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  useApi,
  useCatalogoDePermissoes,
  usePermissoesDaPessoa,
  usePessoasDasPermissoes,
} from '@/shared/api/consultas';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import {
  Avatar,
  Botao,
  Campo,
  CampoSelecao,
  Cartao,
  FiltroPilula,
  Icone,
  Interruptor,
  Lista,
  ListaLinha,
  Segmentado,
  Selo,
  idDoPainel,
  type NomeDoIcone,
} from '@/shared/ui';
import { nomeDoPapel } from '@/entities/acesso/papel';
import { useMe, useSessao } from '@/shared/sessao/contexto';
import {
  NIVEIS,
  NOME_DO_ALCANCE,
  acoesDoCatalogo,
  alcancesDoPapel,
  alcancesQuePodeDar,
  alternarComo,
  assuntosComAlcance,
  corpoParaSalvar,
  escolherAlcance,
  estadoDa,
  oQueFaltaLigar,
  oQueNaoPodeDar,
  podeLigar,
  quantasAlteracoes,
  seloDa,
  temAcao,
  type Alcance,
  type AcaoNaTela,
  type EstadoDasPermissoes,
  type QuemEdita,
} from '../lib/permissoes-da-pessoa';
import './permissoes.css';

/** Os papéis que a seção mostra, na ordem dos filtros. */
const PAPEIS: readonly PapelDaOrganizacao[] = ['admin_tenant', 'gestor', 'vendedor', 'agente'];

const ICONE_DA_ABA: Readonly<Record<CatalogoDePermissoes['abas'][number]['aba'], NomeDoIcone>> = {
  atendimento: 'atendimento',
  comercial: 'funil',
  organizacao: 'configuracoes',
  integracoes: 'automacao',
};

const SALVANDO = 'Salvando as alterações.';

export function SecaoPermissoes(): ReactNode {
  const [aberta, definirAberta] = useState<PessoaDasPermissoes | null>(null);
  /** O rascunho de cada pessoa, pelo id do vínculo. Sem rascunho, vale o salvo. */
  const [rascunhos, definirRascunhos] = useState<Readonly<Record<string, EstadoDasPermissoes>>>({});

  const mudarRascunho = (id: string, estado: EstadoDasPermissoes | null) =>
    definirRascunhos((atuais) => {
      const novos = { ...atuais };
      if (estado) novos[id] = estado;
      else delete novos[id];
      return novos;
    });

  return (
    <section className="admin__secao permissoes" aria-label="Permissões">
      {aberta ? (
        <TelaDaPessoa
          key={aberta.id}
          pessoa={aberta}
          rascunho={rascunhos[aberta.id] ?? null}
          aoMudar={(estado) => mudarRascunho(aberta.id, estado)}
          aoVoltar={() => definirAberta(null)}
        />
      ) : (
        <EscolherPessoa naoSalvas={rascunhos} aoAbrir={definirAberta} />
      )}
    </section>
  );
}

// ── 1 · Escolher a pessoa ──────────────────────────────────────────────────

function EscolherPessoa({
  naoSalvas,
  aoAbrir,
}: {
  naoSalvas: Readonly<Record<string, unknown>>;
  aoAbrir: (pessoa: PessoaDasPermissoes) => void;
}): ReactNode {
  const consulta = usePessoasDasPermissoes();
  const [busca, definirBusca] = useState('');
  const [filtro, definirFiltro] = useState<PapelDaOrganizacao | 'todos'>('todos');

  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={5} rotulo="Carregando as pessoas" />;
  }
  // Só sem nada na tela: com a lista carregada, uma releitura que falhou não a apaga.
  if (consulta.data === undefined) {
    return (
      <Erro
        erro={consulta.error}
        aoRepetir={() => void consulta.refetch()}
        titulo="Não foi possível carregar as pessoas"
      />
    );
  }

  const todas = consulta.data.itens;
  const termo = busca.trim().toLowerCase();
  const visiveis = todas
    .filter((p) => filtro === 'todos' || p.papel === filtro)
    .filter(
      (p) =>
        !termo || p.nome.toLowerCase().includes(termo) || p.email.toLowerCase().includes(termo),
    );

  return (
    <>
      <p className="admin__ajuda">
        Escolha a pessoa para ver e mudar o que ela pode fazer. Cada pessoa parte do padrão do papel
        dela.
      </p>

      {todas.length === 0 ? (
        <Vazio
          titulo="Ninguém para ajustar ainda"
          porque="As permissões são de quem trabalha na organização. Convide a primeira pessoa em Pessoas."
        />
      ) : (
        <>
          <div className="permissoes__filtros">
            <div className="permissoes__busca">
              <Campo
                icone="buscar"
                rotuloOculto="Buscar pessoa"
                placeholder="Buscar por nome ou e-mail"
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
              />
            </div>
            <div role="group" aria-label="Filtrar por papel" className="permissoes__papeis">
              <FiltroPilula
                ligado={filtro === 'todos'}
                aoAlternar={() => definirFiltro('todos')}
                contagem={todas.length}
              >
                Todos
              </FiltroPilula>
              {PAPEIS.map((papel) => (
                <FiltroPilula
                  key={papel}
                  ligado={filtro === papel}
                  aoAlternar={() => definirFiltro(papel)}
                  contagem={todas.filter((p) => p.papel === papel).length}
                >
                  {nomeDoPapel(papel)}
                </FiltroPilula>
              ))}
            </div>
          </div>

          <Cartao como="section" aria-label="Pessoas" parte="permissoes__pessoas">
            <span className="permissoes__contagem">
              {visiveis.length === 1 ? '1 pessoa' : `${visiveis.length} pessoas`}
            </span>
            {visiveis.length === 0 ? (
              <p className="permissoes__nenhuma">Ninguém com esse nome neste filtro.</p>
            ) : (
              <Lista>
                {visiveis.map((pessoa) => {
                  const estado =
                    pessoa.id in naoSalvas
                      ? { tom: 'atencao' as const, texto: 'Não salvo' }
                      : pessoa.estado === 'personalizada'
                        ? { tom: 'info' as const, texto: 'Personalizada' }
                        : { tom: 'neutro' as const, texto: 'Padrão do papel' };
                  return (
                    <ListaLinha
                      key={pessoa.id}
                      inicio={<Avatar nome={pessoa.nome} tamanho={36} />}
                      nome={pessoa.nome}
                      previa={`${nomeDoPapel(pessoa.papel)} · ${pessoa.email}`}
                      direita={
                        <span className="permissoes__estado">
                          <Selo tom={estado.tom}>{estado.texto}</Selo>
                          <Icone nome="direita" />
                        </span>
                      }
                      aoAbrir={() => aoAbrir(pessoa)}
                      // O selo entra no nome: o rótulo do alvo cobre o conteúdo da linha.
                      rotulo={`Abrir as permissões de ${pessoa.nome}, ${estado.texto}`}
                    />
                  );
                })}
              </Lista>
            )}
          </Cartao>
        </>
      )}

      <p className="permissoes__rodape">
        Suporte Cliqfy, Super Admin e a conta Automação não aparecem aqui: só a Cliqfy altera esses
        acessos.
      </p>
    </>
  );
}

// ── 2 · Permissões da pessoa ───────────────────────────────────────────────

function TelaDaPessoa({
  pessoa,
  rascunho,
  aoMudar,
  aoVoltar,
}: {
  pessoa: PessoaDasPermissoes;
  rascunho: EstadoDasPermissoes | null;
  aoMudar: (estado: EstadoDasPermissoes | null) => void;
  aoVoltar: () => void;
}): ReactNode {
  const consulta = usePermissoesDaPessoa(pessoa.id);
  const catalogo = useCatalogoDePermissoes();

  const caminho = (
    <nav aria-label="Caminho" className="permissoes__caminho">
      <Botao variante="fantasma" miudo onClick={aoVoltar}>
        <Icone nome="voltar" />
        Pessoas
      </Botao>
      <span aria-hidden="true" className="permissoes__separador">
        /
      </span>
      <span aria-current="page">{pessoa.nome}</span>
    </nav>
  );

  if (consulta.isPending || catalogo.isPending) {
    return (
      <>
        {caminho}
        <Carregando forma="lista" linhas={4} rotulo="Carregando as permissões" />
      </>
    );
  }
  if (consulta.data === undefined || catalogo.data === undefined) {
    return (
      <>
        {caminho}
        <Erro
          erro={consulta.error ?? catalogo.error}
          aoRepetir={() => {
            void consulta.refetch();
            void catalogo.refetch();
          }}
          titulo="Não foi possível carregar as permissões"
        />
      </>
    );
  }

  return (
    <>
      {caminho}
      <Ajuste
        dados={consulta.data}
        catalogo={catalogo.data}
        rascunho={rascunho}
        aoMudar={aoMudar}
      />
    </>
  );
}

function Ajuste({
  dados,
  catalogo,
  rascunho,
  aoMudar,
}: {
  dados: PermissoesDaPessoa;
  catalogo: CatalogoDePermissoes;
  rascunho: EstadoDasPermissoes | null;
  aoMudar: (estado: EstadoDasPermissoes | null) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { recarregarMe } = useSessao();
  const me = useMe();
  const prefixo = useId();
  const [aba, definirAba] = useState(catalogo.abas[0]?.aba ?? 'atendimento');
  const [aviso, definirAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const { pessoa, edicao } = dados;
  const quem: QuemEdita = { permissoes: me?.permissoes ?? [], limitada: edicao.limitada };
  const todas = acoesDoCatalogo(catalogo);
  const comAlcance = assuntosComAlcance(catalogo);
  const doPapel = alcancesDoPapel(catalogo, pessoa.papel);
  const inicial = estadoDa(dados.efetivas, doPapel);
  const padrao = estadoDa(dados.padrao, doPapel);
  const estado = rascunho ?? inicial;
  const pendentes = quantasAlteracoes(estado, inicial, comAlcance);
  const personalizada = quantasAlteracoes(estado, padrao, comAlcance) > 0;
  const primeiroNome = pessoa.nome.split(' ')[0] || pessoa.nome;

  const salvar = useMutation({
    mutationFn: async (corpo: SalvarPermissoesDaPessoa) => {
      if (!api) throw new Error('Sem sessão.');
      return api.salvarPermissoesDaPessoa({ caminho: { id: pessoa.id }, corpo });
    },
    onSuccess: (resposta) => {
      // A resposta já é a pessoa salva: a tela mostra o que a API guardou.
      cache.setQueryData(chaves.permissoesDaPessoa(pessoa.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.pessoasDasPermissoes(), exact: true });
      aoMudar(null);
      definirAviso({
        tom: 'ok',
        texto: `Alterações salvas. ${primeiroNome} passa a ter as novas permissões.`,
      });
      // Quem salvou pode ter mexido nas próprias permissões (o Administrador pode).
      void recarregarMe();
    },
    onError: (erro: unknown) => {
      // O papel mudou com a tela aberta: o rascunho foi escolhido sobre o papel antigo.
      if (erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_ESTADO') {
        aoMudar(null);
        void cache.invalidateQueries({ queryKey: chaves.permissoesDaPessoa(pessoa.id) });
        void cache.invalidateQueries({ queryKey: chaves.pessoasDasPermissoes(), exact: true });
      }
      definirAviso({
        tom: 'erro',
        texto: recadoDeErro(erro, { generica: 'Não foi possível salvar. Tente de novo.' }),
      });
    },
  });

  const mudar = (novo: EstadoDasPermissoes) => {
    definirAviso(null);
    aoMudar(quantasAlteracoes(novo, inicial, comAlcance) === 0 ? null : novo);
  };

  /** Por que este toggle não muda, ou `null`. */
  const travaDe = (acao: AcaoNaTela): string | null => {
    const k = `${acao.recurso}:${acao.acao}`;
    if (!edicao.pode) return edicao.motivo ?? 'Você não muda as permissões desta pessoa.';
    if (salvar.isPending) return SALVANDO;
    if (acao.trava !== null) return acao.trava;
    if (estado.ligadas.has(k)) return null;
    if (!inicial.ligadas.has(k) && !podeLigar(quem, acao.recurso, acao.acao)) {
      return 'Você só pode dar o que também tem, e esta permissão você não tem.';
    }
    const falta = oQueFaltaLigar(estado, acao)[0];
    if (falta) {
      const nome = todas.find((t) => t.recurso === falta.recurso && t.acao === falta.acao)?.rotulo;
      return nome ? `Ligue "${nome}" primeiro.` : 'Ligue primeiro o que esta permissão exige.';
    }
    return null;
  };

  const travaDoPadrao = !edicao.pode
    ? (edicao.motivo ?? 'Você não muda as permissões desta pessoa.')
    : salvar.isPending
      ? SALVANDO
      : !personalizada
        ? 'Já está no padrão do papel.'
        : oQueNaoPodeDar(quem, inicial, padrao, comAlcance).length > 0
          ? 'O padrão do papel liga o que você não pode dar. Quem é Administrador faz essa volta.'
          : null;

  const abaAtual = catalogo.abas.find((a) => a.aba === aba) ?? catalogo.abas[0];

  return (
    <>
      <Cartao como="section" aria-label="Pessoa escolhida" parte="permissoes__pessoa">
        <Avatar nome={pessoa.nome} tamanho={48} cheio />
        <div className="permissoes__quem">
          <div className="permissoes__nome-e-selos">
            <h2 className="permissoes__nome">{pessoa.nome}</h2>
            <Selo tom="neutro">{nomeDoPapel(pessoa.papel)}</Selo>
            {personalizada && <Selo tom="info">Personalizada</Selo>}
          </div>
          <span className="permissoes__contato">
            {pessoa.email} · Carteira: {NOME_DO_ALCANCE[pessoa.carteira]}
          </span>
        </div>
        <Botao
          variante="fantasma"
          onClick={() => mudar(padrao)}
          desabilitado={travaDoPadrao !== null}
          motivo={travaDoPadrao ?? ''}
        >
          Voltar ao padrão do papel
        </Botao>
      </Cartao>

      {!edicao.pode && edicao.motivo && (
        <p className="admin__nota admin__nota--info">{edicao.motivo}</p>
      )}

      <Segmentado
        como="abas"
        aparencia="sublinhado"
        rotulo="Área das permissões"
        idDasAbas={prefixo}
        escolhida={abaAtual?.aba ?? aba}
        aoEscolher={definirAba}
        opcoes={catalogo.abas.map((a) => ({
          valor: a.aba,
          rotulo: a.rotulo,
          icone: ICONE_DA_ABA[a.aba],
          contagem: a.assuntos.reduce(
            (n, assunto) =>
              n +
              assunto.acoes.filter((x) => estado.ligadas.has(`${assunto.recurso}:${x.acao}`))
                .length,
            0,
          ),
        }))}
      />

      {abaAtual && (
        <div
          role="tabpanel"
          id={idDoPainel(prefixo, abaAtual.aba)}
          aria-labelledby={`${prefixo}-${abaAtual.aba}`}
          className="permissoes__assuntos"
        >
          {abaAtual.assuntos.map((assunto) => (
            <Assunto
              key={assunto.recurso}
              assunto={assunto}
              estado={estado}
              padrao={padrao}
              todas={todas}
              comAlcance={comAlcance}
              travaDe={travaDe}
              alcances={alcancesQuePodeDar(quem, inicial, assunto.recurso)}
              limitada={quem.limitada}
              travado={!edicao.pode || salvar.isPending}
              aoAlternar={(acao) =>
                mudar(
                  alternarComo(quem, inicial, estado, todas, comAlcance, acao.recurso, acao.acao),
                )
              }
              aoEscolherAlcance={(alcance) =>
                mudar(escolherAlcance(estado, assunto.recurso, alcance))
              }
            />
          ))}
        </div>
      )}

      {aviso?.tom === 'erro' && (
        <p className="permissoes__erro" role="alert">
          {aviso.texto}
        </p>
      )}

      {edicao.pode && (
        <div className="permissoes__barra">
          <p className="permissoes__barra-texto" role="status">
            {pendentes > 0 ? (
              <>
                <strong>
                  {pendentes === 1 ? '1 alteração não salva' : `${pendentes} alterações não salvas`}
                </strong>{' '}
                <span className="permissoes__barra-apoio">
                  em {pessoa.nome}. Nada muda até você salvar.
                </span>
              </>
            ) : (
              <>
                <Icone nome="check" />
                <span className="permissoes__barra-apoio">
                  {aviso?.tom === 'ok'
                    ? aviso.texto
                    : `Tudo salvo nas permissões de ${primeiroNome}.`}
                </span>
              </>
            )}
          </p>
          {pendentes > 0 && (
            <div className="permissoes__barra-acoes">
              <Botao
                variante="fantasma"
                onClick={() => {
                  definirAviso(null);
                  aoMudar(null);
                }}
                desabilitado={salvar.isPending}
                motivo={SALVANDO}
              >
                Descartar
              </Botao>
              <Botao
                variante="primario"
                onClick={() =>
                  salvar.mutate(corpoParaSalvar(pessoa.papel, estado, padrao, todas, comAlcance))
                }
                desabilitado={salvar.isPending}
                motivo={SALVANDO}
              >
                {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
              </Botao>
            </div>
          )}
        </div>
      )}
    </>
  );
}

type AssuntoDoCatalogo = CatalogoDePermissoes['abas'][number]['assuntos'][number];

/** Um cartão por assunto: o "Até onde vale" e os toggles. */
function Assunto({
  assunto,
  estado,
  padrao,
  todas,
  comAlcance,
  travaDe,
  alcances,
  limitada,
  travado,
  aoAlternar,
  aoEscolherAlcance,
}: {
  assunto: AssuntoDoCatalogo;
  estado: EstadoDasPermissoes;
  padrao: EstadoDasPermissoes;
  todas: readonly AcaoNaTela[];
  comAlcance: readonly string[];
  travaDe: (acao: AcaoNaTela) => string | null;
  /** Os alcances que quem edita pode escolher aqui. */
  alcances: readonly Alcance[];
  limitada: boolean;
  /** Nada muda: quem edita não pode, ou está salvando. */
  travado: boolean;
  aoAlternar: (acao: AcaoNaTela) => void;
  aoEscolherAlcance: (alcance: Alcance) => void;
}): ReactNode {
  const titulo = useId();
  const comAcao = temAcao(estado, assunto.recurso);
  const teto = alcances[alcances.length - 1];
  const ajuda = !comAcao
    ? 'Vale quando alguma ação deste assunto estiver ligada.'
    : limitada && teto && teto !== 'tenant'
      ? `Vale o menor entre isto e a carteira da pessoa. Você dá até "${NOME_DO_ALCANCE[teto]}".`
      : 'Vale o menor entre isto e a carteira da pessoa.';

  return (
    <Cartao como="section" variante="folgado" aria-labelledby={titulo} parte="permissoes__assunto">
      <div className="permissoes__assunto-cabeca">
        <h3 id={titulo} className="permissoes__assunto-nome">
          {assunto.nome}
        </h3>
        <p className="permissoes__assunto-cobre">{assunto.cobre}</p>
      </div>

      {comAlcance.includes(assunto.recurso) && (
        <CampoSelecao
          rotulo="Até onde vale"
          rotuloVersal
          tamanho="miudo"
          ajuda={ajuda}
          value={estado.alcances[assunto.recurso] ?? 'propria'}
          onChange={(e) => aoEscolherAlcance(e.target.value as Alcance)}
          disabled={travado || !comAcao}
        >
          {NIVEIS.map((nivel) => (
            <option key={nivel} value={nivel} disabled={!alcances.includes(nivel)}>
              {NOME_DO_ALCANCE[nivel]}
            </option>
          ))}
        </CampoSelecao>
      )}

      <ul className="permissoes__acoes">
        {assunto.acoes.map((doCatalogo) => {
          const acao = todas.find(
            (a) => a.recurso === assunto.recurso && a.acao === doCatalogo.acao,
          );
          if (!acao) return null;
          const k = `${acao.recurso}:${acao.acao}`;
          const ligado = estado.ligadas.has(k);
          const motivo = travaDe(acao);
          const selo = seloDa(estado, padrao, acao.recurso, acao.acao);
          // Desligada porque o que ela exige está desligado: o selo repetiria o do pré-requisito.
          const emCadeia = !ligado && oQueFaltaLigar(estado, acao).length > 0;
          return (
            <li key={acao.acao} className="permissoes__acao">
              <div className="permissoes__toggle">
                <Interruptor
                  ligado={ligado}
                  aoAlternar={() => aoAlternar(acao)}
                  rotulo={acao.rotulo}
                  contexto={assunto.nome}
                  {...(acao.descricao ? { descricao: acao.descricao } : {})}
                  {...(motivo ? { desabilitado: true, motivo } : {})}
                />
              </div>
              {selo === 'alem' && <Selo tom="info">Além do papel</Selo>}
              {selo === 'tirada' && !emCadeia && <Selo tom="atencao">Tirada do papel</Selo>}
            </li>
          );
        })}
      </ul>
    </Cartao>
  );
}
